import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface WidgetDistributionMetadata {
  name: string;
  slug: string;
  archive_slug?: string | null;
  version: string;
  zip_url: string;
  sha256: string;
  released_at: string;
  docs_url: string;
  changelog_url: string;
  compatibility_notes?: string | null;
}

const widgetMetadataFallbackPaths = [
  `${process.env.PUBLIC_URL || ''}/merchant-integration/rizzpay-woocommerce-widget-metadata.json`,
  `${process.env.PUBLIC_URL || ''}/merchant-integration/widget-metadata.json`,
];

class WidgetDistributionService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private normalizeMetadata(raw: Partial<WidgetDistributionMetadata>): WidgetDistributionMetadata {
    const metadata: WidgetDistributionMetadata = {
      name: raw.name?.trim() || '',
      slug: raw.slug?.trim() || '',
      archive_slug: raw.archive_slug?.trim() || null,
      version: raw.version?.trim() || '',
      zip_url: raw.zip_url?.trim() || '',
      sha256: raw.sha256?.trim() || '',
      released_at: raw.released_at?.trim() || '',
      docs_url: raw.docs_url?.trim() || '',
      changelog_url: raw.changelog_url?.trim() || '',
      compatibility_notes: raw.compatibility_notes?.trim() || null,
    };

    if (
      !metadata.name
      || !metadata.slug
      || !metadata.version
      || !metadata.zip_url
      || !metadata.sha256
      || !metadata.released_at
      || !metadata.docs_url
      || !metadata.changelog_url
    ) {
      throw new Error('Widget metadata is missing required fields');
    }

    return metadata;
  }

  private async getStaticWidgetMetadata(): Promise<WidgetDistributionMetadata> {
    let lastFallbackError: unknown = null;

    for (const path of widgetMetadataFallbackPaths) {
      try {
        const response = await axios.get<WidgetDistributionMetadata>(path, {
          headers: {
            'Content-Type': 'application/json',
          },
          params: {
            v: Date.now().toString(),
          },
        });
        return this.normalizeMetadata(response.data);
      } catch (fallbackError) {
        lastFallbackError = fallbackError;
      }
    }

    if (lastFallbackError) {
      throw lastFallbackError;
    }

    throw new Error('Widget metadata unavailable');
  }

  async getWidgetMetadata(): Promise<WidgetDistributionMetadata> {
    try {
      return await this.getStaticWidgetMetadata();
    } catch (staticError) {
      console.warn('Static widget metadata unavailable; trying API endpoint.', staticError);
    }

    const apiUrl = `${API_CONFIG.BASE_URL}/settings/widget-distribution`;

    try {
      const response = await axios.get<WidgetDistributionMetadata>(apiUrl, {
        headers: this.getAuthHeaders(),
      });

      return this.normalizeMetadata(response.data);
    } catch (apiError) {
      if (axios.isAxiosError(apiError) && apiError.response?.status) {
        console.warn('Widget metadata API endpoint returned an error.', apiError.response.status);
      }
      throw apiError;
    }
  }
}

export const widgetDistributionService = new WidgetDistributionService();
